import './utils/kickstart';

// Import packages
import { max, sum, zip } from 'd3-array';
import { nest } from 'd3-collection';
import debounce from 'lodash.debounce';
import { timeParse } from 'd3-time-format';
import { getSortByString } from './utils/sorters';
import { dateFormat, pctFormat } from './utils/d3-utils';

// Translation
import { __ } from './utils/i18n';

// Navbar + linklist buttons
import './components/navbar';
import './components/linklist';

// Colors
import {
  CASES_LIGHT_BLUE,
  CASES_DARK_BLUE,
  DEATHS_LIGHT_GREY,
  DEATHS_DARK_GREY,
  DEATHS_DARKEST_GREY,
  VACCINES_GREEN,
  VACCINES_GREEN_LIGHT,
} from './colors';
import { movingAverage } from './utils/moving-average';

// Generic charts
import BarTimeseries from './components/BarTimeseries.svelte';
import AreaTimeseries from './components/AreaTimeseries.svelte';
import LineTimeseries from './components/LineTimeseries.svelte';
import StackedBarTimeseries from './components/StackedBarTimeseries.svelte';

// County mini multiples
import countyCompare from './components/countyCompare';

// Maps
import CountiesDashboard from './components/countiesMap/CountiesDashboard.svelte';

// Ranking tables
import StateCasesTable from './components/hopkins/StateCasesTable.svelte';
import StateDeathsTable from './components/hopkins/StateDeathsTable.svelte';

// Demographics
import { boot as bootCasesByAge } from './components/demographics/casesByAge';
import { boot as bootDeathsByAge } from './components/demographics/deathsByAge';
import { boot as bootDeathsByRace } from './components/demographics/deathsByRace';
import { boot as bootRaceTimeseries } from './components/demographics/raceTimeseries';

// Changelog
import Changelog from './components/Changelog.svelte';

// Data
import { slugify } from './utils/slugify';
import { dateParser } from './utils/d3-utils';
import { loadJsonScript } from './utils/load-json-script';

// Utils
import { lazyScript } from './utils/lazy-script';

const lazyLoader = lazyScript();

// Import data
const statewideTimeSeriesData = loadJsonScript('statewide-timeseries-data').map(
  (d) => {
    d.date = dateParser(d.date);
    return d;
  }
);
const countiesData = loadJsonScript('counties-timeseries-data');
const missingDemographics = loadJsonScript('demographics-data-missing');
const demographicsTimeseries = loadJsonScript('demographics-timeseries').map(
  (d) => {
    d.date = dateParser(d.date);
    return d;
  }
);
const countiesMetadata = loadJsonScript('counties-metadata');
const otherStatesTimeSeriesData = loadJsonScript('other-states-timeseries');
const regionalTimeseries = loadJsonScript('regions-timeseries');
const regionalCasesTimeseries = loadJsonScript('regions-cases-timeseries');

// variants
const variantsTimeseries = loadJsonScript(
  'vaccine-variants-statewide-timeseries'
);

//prisons data
const prisonsData = loadJsonScript('prisons-timeseries').map((d) => {
  d.date = dateParser(d.date);
  return d;
});

// vaccine data
const vaccineCumulativePercentage = loadJsonScript(
  'vaccine-cumulative-percentage'
).map((d) => {
  d.date = dateParser(d.date);
  d.pct_fully_vaccinated = d.pct_fully_vaccinated || 0;
  d.pct_partially_vaccinated = d.pct_partially_vaccinated;
  return d;
});

// Boot top-line charts
const casesCumTarget = document.getElementById('cases-timeseries-chart');
new LineTimeseries({
  target: casesCumTarget,
  props: {
    title: __('Cumulative cases'),
    data: statewideTimeSeriesData,
    width: casesCumTarget.offsetWidth,
    margin: { top: 20, right: 80, bottom: 25, left: 70 },
    attrs: [
      {
        headline: __("Cumulative <span class='cases-blue'>cases</span>"),
        label: __('Cases'),
        value: 'confirmed_and_probable_cases',
        color: '#3995b7',
      },
      {
        headline: __("Cumulative <span class='deaths-gray'>deaths</span>"),
        label: __('Deaths'),
        value: 'confirmed_deaths',
        color: '#829db0',
      },
    ],
    sourceline: __('California Department of Public Health'),
  },
});

const newCasesTarget = document.getElementById('new-cases-chart');
const newCasesData = statewideTimeSeriesData.filter((d) => !d.in_progress);
newCasesData.forEach((d) => {
  if (d.new_confirmed_cases < 0) {
    d.new_confirmed_cases = 0;
  }
});

// newCasesData.forEach((d) => {
//   if (
//     //d.date == 'Mon Jan 03 2022 00:00:00 GMT-0800 (Pacific Standard Time)' ||
//     d.date == 'Sun Jan 09 2022 00:00:00 GMT-0800 (Pacific Standard Time)'
//   ) {
//     d.class = 'incomplete';
//   } else {
//     d.class = 'complete';
//   }
// });

new BarTimeseries({
  target: newCasesTarget,
  props: {
    title: __('New cases'),
    data: newCasesData,
    width: newCasesTarget.offsetWidth,
    margin: { top: 20, right: 10, bottom: 25, left: 52 },
    name: 'cases',
    attrs: [
      {
        headline: __('New <span class="cases-blue">cases</span> by day'),
        label: __('Cases'),
        value: 'new_confirmed_and_probable_cases',
        barColor: CASES_LIGHT_BLUE,
        lineColor: CASES_DARK_BLUE,
      },
    ],
    labelTarget: [dateParser('2020-06-01'), 3000],
    sourceline: null,
  },
});

const newDeathsTarget = document.getElementById('new-deaths-chart');
const newDeathsData = statewideTimeSeriesData.filter((d) => !d.in_progress);
new BarTimeseries({
  target: newDeathsTarget,
  props: {
    title: __('New deaths'),
    data: newDeathsData,
    width: newDeathsTarget.offsetWidth,
    margin: { top: 20, right: 10, bottom: 25, left: 45 },
    name: 'deaths',
    attrs: [
      {
        headline: __('<span class="deaths-gray">Deaths</span> by day'),
        label: __('Deaths'),
        value: 'new_confirmed_deaths',
        barColor: DEATHS_LIGHT_GREY,
        lineColor: DEATHS_DARK_GREY,
      },
    ],
    sourceline: null,
  },
});

// Nursing homes chart
const shareTarget = document.getElementById('nursinghomes-chart');
const shareData = statewideTimeSeriesData.filter((d) => d.deaths_nursinghomes);
shareData.forEach((d) => {
  d['other_deaths'] = d['confirmed_deaths'] - d['deaths_nursinghomes'];
  d['other_confirmed_cases'] =
    d['confirmed_and_probable_cases'] - d['confirmed_cases_nursinghomes'];
});
new AreaTimeseries({
  target: shareTarget,
  props: {
    title: __('Nursing home percentage of cases and deaths as of January 2023'),
    data: shareData,
    width: shareTarget.offsetWidth,
    attrs: [
      {
        headline: __(
          'Cases at <span class="cases-blue">nursing homes</span> vs. <span class="cases-light-blue">elsewhere</span> as of January 2023'
        ),
        label: __('Cases'),
        value: 'cases',
        maxValue: max(shareData, (d) => d.confirmed_and_probable_cases),
        layers: [
          {
            value: 'confirmed_cases_nursinghomes',
            color: '#3995b7',
          },
          {
            value: 'other_confirmed_cases',
            color: '#c0e9f3',
          },
        ],
      },
      {
        headline: __(
          'Deaths at <span class="deaths-gray">nursing homes</span> vs. <span class="deaths-light-gray">elsewhere</span> as of January 2023'
        ),
        label: __('Deaths'),
        value: 'deaths',
        maxValue: max(shareData, (d) => d.confirmed_deaths),
        layers: [
          {
            value: 'deaths_nursinghomes',
            color: '#829db0',
          },
          {
            value: 'other_deaths',
            color: '#c2cad3',
          },
        ],
      },
    ],
    selectedAttrIndex: 1,
    sourceline: __('California Department of Public Health'),
  },
});

// Testing charts
// const newTestsTarget = document.getElementById('testing-timeseries-chart');
// const newTestsData = statewideTimeSeriesData.filter(
//   (d) => d.date > dateParser('2020-04-29') && d.new_tests
// );
// newTestsData.forEach((d) => {
//   if (d.new_tests < 0) {
//     d.new_tests = 0;
//   }
// });

// newTestsData.forEach((d, i) => {
//   d.class = i < newTestsData.length - 3 ? 'complete' : 'incomplete';
// });

// new BarTimeseries({
//   target: newTestsTarget,
//   props: {
//     title: __('New tests by day'),
//     data: newTestsData,
//     width: newTestsTarget.offsetWidth,
//     margin: { top: 20, right: 10, bottom: 25, left: 70 },
//     name: 'tests',
//     attrs: [
//       {
//         headline: __("New <span class='cases-blue'>tests</span> by day"),
//         label: __('Tests'),
//         value: 'new_tests',
//         barColor: CASES_LIGHT_BLUE,
//         lineColor: CASES_DARK_BLUE,
//         lineFunc: (data) => {
//           return zip(
//             data.map((d) => d.date),
//             movingAverage(
//               data.map((d) => d.value),
//               7
//             ).slice(0, -3)
//           );
//         },
//       },
//     ],
//     footnote: __(
//       'Due to delays in data collection, totals for the most recent days are incomplete.'
//     ),
//     sourceline: __('California Department of Public Health'),
//   },
// });

// const testRateTarget = document.getElementById('testing-positive-rate-chart');
// new LineTimeseries({
//   target: testRateTarget,
//   props: {
//     title: __('Positive test rate, seven-day average'),
//     data: statewideTimeSeriesData.filter(
//       (d) =>
//         d.date > dateParser('2020-04-29') &&
//         d.positive_test_rate_seven_day_total
//     ),
//     width: testRateTarget.offsetWidth,
//     margin: { top: 20, right: 65, bottom: 25, left: 35 },
//     attrs: [
//       {
//         headline: __(
//           __(
//             "Positive <span class='cases-blue'>test</span> rate, seven-day average"
//           )
//         ),
//         label: 'Positive test rate',
//         value: 'positive_test_rate_seven_day_total',
//         color: '#3995b7',
//       },
//     ],
//     sourceline: __('California Department of Public Health'),
//     formatLastValue: (d) => `${(d.value * 100).toFixed(1)}%`,
//     formatYTicks: pctFormat,
//   },
// });

// Boot mini case charts
const renderRegionCompare = countyCompare(
  regionalCasesTimeseries,
  'regions-compare-container',
  'Regions',
  (obj, idx) => `${idx + 1}. ${__(obj.region)}`
);
renderRegionCompare();

const renderCountyCompare = countyCompare(
  countiesData,
  'counties-compare-container',
  'Counties',
  (obj, idx) =>
    `<a href="./${slugify(obj.county)}-county/">${idx + 1}. ${obj.county}</a>`
);
renderCountyCompare();

// Boot maps
new CountiesDashboard({
  target: document.getElementById('california-map-container'),
  props: {
    data: countiesData.sort(
      (a, b) =>
        b['confirmed_and_probable_cases'] - a['confirmed_and_probable_cases']
    ),
    metadata: countiesMetadata,
    last_call: window.LAST_CALL,
  },
});

// Patients charts
const patientsTarget = document.getElementById('patients-chart');
const patientsData = statewideTimeSeriesData.filter(
  (d) => d.total_acute_patients
);
const patientsMaxValue = max(
  patientsData.map((d) => {
    return sum([d.total_icu_patients, d.total_acute_patients]);
  })
);
new AreaTimeseries({
  target: patientsTarget,
  props: {
    title: __('Cumulative cases'),
    data: patientsData,
    width: patientsTarget.offsetWidth,
    attrs: [
      {
        headline: __(
          '<span class="cases-blue">Intensive care</span> and <span class="cases-light-blue">other</span> hospitalized patients'
        ),
        label: __('Positive'),
        value: 'positive',
        maxValue: patientsMaxValue,
        layers: [
          {
            value: 'icu_positive_patients',
            color: '#3995b7',
          },
          {
            value: 'acute_positive_patients',
            color: '#c0e9f3',
          },
        ],
      },
      {
        headline: __(
          '<span class="cases-blue">Intensive care</span> and <span class="cases-light-blue">other</span> hospitalized patients'
        ),
        label: __('Suspected'),
        value: 'suspected',
        maxValue: patientsMaxValue,
        layers: [
          {
            value: 'icu_suspected_patients',
            color: '#3995b7',
          },
          {
            value: 'acute_suspected_patients',
            color: '#c0e9f3',
          },
        ],
      },
      {
        headline: __(
          '<span class="cases-blue">Intensive care</span> and <span class="cases-light-blue">other</span> hospitalized patients'
        ),
        label: __('Both'),
        value: 'both',
        maxValue: patientsMaxValue,
        layers: [
          {
            value: 'total_icu_patients',
            color: '#3995b7',
          },
          {
            value: 'total_acute_patients',
            color: '#c0e9f3',
          },
        ],
      },
    ],
    sourceline: __(
      '<a href="https://data.ca.gov/dataset/covid-19-hospital-data">California Department of Public Health</a>'
    ),
  },
});

// Vaccines
const vaccineTarget = document.getElementById('vaccine-cumulative-chart');
new AreaTimeseries({
  target: vaccineTarget,
  props: {
    data: vaccineCumulativePercentage,
    width: vaccineTarget.offsetWidth,
    margin: { top: 20, right: 0, bottom: 35, left: 35 },
    attrs: [
      {
        headline: __(
          'Percentage of Californians who are <span class="vaccines-light-green">partially</span> or <span class="vaccines-green">fully</span> vaccinated'
        ),
        label: __('Percent vaccinated'),
        value: 'percent_vaccinated',
        maxValue: 1,
        layers: [
          {
            value: 'pct_fully_vaccinated',
            color: VACCINES_GREEN,
          },
          {
            value: 'pct_partially_vaccinated',
            color: VACCINES_GREEN_LIGHT,
          },
        ],
      },
    ],
    formatYTicks: pctFormat,
    sourceline: __(
      '<a href="https://data.ca.gov/dataset/covid-19-vaccine-progress-dashboard-data">California Department of Public Health</a>'
    ),
  },
});

// Variants chart
const variantsTarget = document.getElementById('variants-timeseries-chart');
const variantsData = variantsTimeseries.map((d) => {
  d.date = dateParser(d.date);
  d.other = 100 - (d.delta + d.alpha + d.gamma + d.epsilon + d.omicron);
  return d;
});
new AreaTimeseries({
  target: variantsTarget,
  props: {
    title: __('Variants'),
    data: variantsData,
    width: variantsTarget.offsetWidth,
    margin: { top: 20, right: 0, bottom: 35, left: 35 },
    attrs: [
      {
        headline: __('Variants among new cases'),
        label: __('Variants'),
        value: 'variants',
        maxValue: 100,
        annotations: [
          {
            x: dateParser('2020-12-31'),
            y: 99,
            text: __('Original strain and other variants'),
            anchor: 'left',
          },
          {
            x: dateParser('2022-03-01'),
            y: 55,
            anchor: 'end',
            text: __('Omicron'),
          },
          {
            x: dateParser('2021-09-01'),
            y: 55,
            text: 'Delta',
          },
          {
            x: dateParser('2021-04-25'),
            y: 35,
            text: 'Alpha',
          },
          {
            x: dateParser('2021-01-30'),
            y: 30,
            text: 'Epsilon',
          },
        ],
        layers: [
          {
            value: 'omicron',
            color: '#b75a36',
          },
          {
            value: 'delta',
            color: 'rgb(76, 144, 169)',
          },
          {
            value: 'alpha',
            color: 'rgb(187, 255, 255)',
          },
          {
            value: 'gamma',
            color: '#00435c',
          },
          {
            value: 'epsilon',
            color: 'rgb(131, 198, 224)',
          },
          {
            value: 'other',
            color: 'rgb(209, 211, 212)',
          },
        ],
      },
    ],
    formatYTicks: (v) => pctFormat(v / 100),
    sourceline: __(
      '<a href="https://www.cdph.ca.gov/Programs/CID/DCDC/Pages/COVID-19/COVID-Variants.aspx">California Department of Public Health</a>'
    ),
  },
});

// Boot demographics
const agesData = loadJsonScript('ages-data');
const casesByAge = bootCasesByAge(agesData);
const deathsByAge = bootDeathsByAge(agesData, missingDemographics);
const deathsByRace = bootDeathsByRace(
  loadJsonScript('demographics-data'),
  missingDemographics
);
const raceTimeseries = bootRaceTimeseries(demographicsTimeseries);

window.addEventListener('resize', debounce(casesByAge, 300));
window.addEventListener('resize', debounce(deathsByAge, 300));
window.addEventListener('resize', debounce(deathsByRace, 300));
window.addEventListener('resize', debounce(raceTimeseries, 300));

//Boot prisons
const prisonsTarget = document.getElementById('prisons-trend-chart');
new LineTimeseries({
  target: prisonsTarget,
  props: {
    title: __('Cumulative cases'),
    data: prisonsData,
    width: prisonsTarget.offsetWidth,
    margin: { top: 20, right: 80, bottom: 25, left: 60 },
    attrs: [
      {
        headline: __("Cumulative <span class='cases-blue'>cases</span>"),
        label: __('Cumulative'),
        value: 'confirmed_cases',
        color: '#3995b7',
      },
      {
        headline: __("Cumulative <span class='deaths-gray'>deaths</span>"),
        label: __('Deaths'),
        value: 'deaths',
        color: '#829db0',
      },
    ],
    sourceline: __('California Department of Corrections and Rehabilitation'),
  },
});

//////California in comparison //////////

// Boot state comparison charts

// const stateCasesTarget = document.getElementById(
//   'california-vs-us-cases-chart'
// );
// // Set our dates
// const minStateDate = dateParser('2020-03-01');
// const maxStateDate = dateParser(otherStatesTimeSeriesData['max_date']);
// // Regroup data into a joined series
// let stateChartData = [];
// statewideTimeSeriesData.forEach((d) => {
//   if (d.date >= minStateDate && d.date <= maxStateDate) {
//     stateChartData.push({
//       date: d.date,
//       california_cases: d.new_confirmed_and_probable_cases,
//       california_deaths: d.new_confirmed_deaths,
//     });
//   }
// });
// stateChartData.forEach((d, i) => {
//   d['other_cases'] = otherStatesTimeSeriesData['new_confirmed_cases'][i];
//   d['total_cases'] = d.other_cases + d.california_cases;
//   d['other_deaths'] = otherStatesTimeSeriesData['new_deaths'][i];
//   d['total_deaths'] = d.other_deaths + d.california_deaths;
// });

// new StackedBarTimeseries({
//   target: stateCasesTarget,
//   props: {
//     data: stateChartData,
//     width: stateCasesTarget.offsetWidth,
//     margin: { top: 20, right: 0, bottom: 35, left: 65 },
//     attrs: [
//       {
//         headline: __(
//           'New cases in <span class="cases-blue">California</span> vs. <span class="cases-light-blue">the rest</span> of the country'
//         ),
//         label: __('Cases'),
//         value: 'cases',
//         maxValue: max(stateChartData, (d) => d.total_cases),
//         layers: [
//           {
//             value: 'california_cases',
//             color: CASES_DARK_BLUE,
//           },
//           {
//             value: 'other_cases',
//             color: CASES_LIGHT_BLUE,
//           },
//         ],
//       },
//     ],
//     sourceline: __(
//       '<a href="https://github.com/CSSEGISandData/COVID-19">Johns Hopkins University CSSE</a>, California Department of Public Health'
//     ),
//   },
// });

// const stateDeathsTarget = document.getElementById(
//   'california-vs-us-deaths-chart'
// );
// new StackedBarTimeseries({
//   target: stateDeathsTarget,
//   props: {
//     data: stateChartData,
//     width: stateCasesTarget.offsetWidth,
//     margin: { top: 20, right: 0, bottom: 35, left: 65 },
//     attrs: [
//       {
//         headline: __(
//           'New deaths in <span class="deaths-darkest-gray">California</span> vs. <span class="deaths-light-gray">the rest</span> of the country'
//         ),
//         label: __('Cases'),
//         value: 'cases',
//         maxValue: max(stateChartData, (d) => d.total_deaths),
//         layers: [
//           {
//             value: 'california_deaths',
//             color: DEATHS_DARKEST_GREY,
//           },
//           {
//             value: 'other_deaths',
//             color: DEATHS_LIGHT_GREY,
//           },
//         ],
//       },
//     ],
//     sourceline: __(
//       '<a href="https://github.com/CSSEGISandData/COVID-19">Johns Hopkins University CSSE</a>, California Department of Public Health'
//     ),
//   },
// });

// // Pull data for state comparisons tables
// const stateData = loadJsonScript('states-timeseries-data');

// // Boot state ranking tables
// new StateCasesTable({
//   target: document.getElementById('state-case-ranking-container'),
//   props: { data: stateData },
// });
// new StateDeathsTable({
//   target: document.getElementById('state-death-ranking-container'),
//   props: { data: stateData },
// });

// Changelog
if (document.documentElement.lang === 'en') {
  new Changelog({
    target: document.getElementById('changelog-container'),
    props: loadJsonScript('changelog-data'),
  });
}

// lazyLoader.add(
//   document.getElementById('map-container-places'),
//   async (target) => {
//     const { render } = await import('./components/places/statewide/index.js');
//     render(target);
//   }
// );

lazyLoader.init({ rootMargin: '500px 0px' });
